import { MutationTree, ActionTree } from 'vuex';
import backend from '@/lib/backend';
import { Query, Design, DesignLayerType, Mutation, Dimension, DesignType, Type } from '@/types';
import { jsonToGraphQLQuery, EnumType } from 'json-to-graphql-query';
import gql from 'graphql-tag';

const designProperriesSelector = `{
    id
    updatedAt
    createdAt
    dimensions
    digitalSize
    type
    thumbnail
    layers {
        createdAt
        updatedAt
        type
        position
        dimensions
        attributes
        image
    }
}`;

interface DesignState {
  price: string;
  design: Design;
  background: string;
  images: Array<string>;
}
export const namespaced = true;
const emptyDesign = {
  dimensions: {
    width: null,
    height: null,
    dpi: 0,
  },
  layers: [],
  type: 'BEHANG',
} as any; // eslint-disable-line @typescript-eslint/no-explicit-any

export const state = (): DesignState => ({
  price: null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  design: (window as any).$store.design || emptyDesign,
  background: null,
  images: [],
});

export const mutations: MutationTree<DesignState> = {
  setDesign(state: DesignState, value: Design): void {
    Object.assign(state.design, value);
  },
  setDesignDimensions(state: DesignState, value: Dimension) {
    state.design.dimensions = { ...state.design.dimensions, ...value };
    this.dispatch('design/calculatePrice');
  },
  setActiveBackground(state: DesignState, value: string) {
    state.background = value;
  },
  setType(state: DesignState, value: DesignType) {
    state.design.type = value;
  },
  setActiveImage(state: DesignState, value: string) {
    state.images.push(value);
  },
  removeActiveImage(state: DesignState, value: string) {
    const index = state.images.indexOf(value);
    if (index >= 0) {
      state.images.splice(index, 1);
    }
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const actions: ActionTree<DesignState, any> = {
  calculatePrice({ state }) {
    if (state.design && state.design.dimensions) {
      let { width, height } = state.design.dimensions;
      const type = (this.state.types.types as Type[]).find((type) => type.name == state.design.type);
      switch (type.name) {
        case 'CANVAS':
          {
            width /= 100;
            height /= 100;

            const { price } = type;
            const basePricing = price.base || 47;
            const canvasPricing = price.canvas || 22;
            const framePricing = price.frame || 13;

            const framePrice = (width * 2 + height * 2) * framePricing;
            const canvasPrice = width * height * canvasPricing;
            const totalPrice = basePricing + framePrice + canvasPrice;

            state.price = totalPrice.toFixed(2).replace('.', ',');
          }
          break;
        case 'BEHANG': {
          let price = (type.price * (width * height)) / Math.pow(100, 2);
          if (price < type.price) {
            price = type.price;
          }
          if (price % 1 !== 0) {
            state.price = `${price.toFixed(2).replace('.', ',')}`;
            return;
          }
          state.price = `${price},-`;
          break;
        }
      }
    }
  },
  async load({ commit }, payload) {
    const result = await backend.query<Query>({
      query: gql`{
                design(where: { id: "${payload}" }) ${designProperriesSelector}
            }`,
    });
    commit('setDesign', result.data.design);
    return result.data.design;
  },
  async save({ commit, state }) {
    const design = (await this.dispatch('canvas/toDesign')) as Design;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const variables: any = {};
    design.layers.forEach((layer, index) => {
      switch (layer.type) {
        case DesignLayerType.Background:
        case DesignLayerType.Image:
          variables[`layer_${index}`] = layer.image;
          break;
      }
    });
    const mutation = {
      __args:
        Object.keys(variables).length > 0
          ? Object.keys(variables).reduce((prev, key) => {
              prev[`$${key}`] = new EnumType('Upload!');
              return prev;
            }, {})
          : undefined,
      createDesign: {
        __args: {
          data: {
            type: new EnumType(state.design.type),
            dimensions: design.dimensions,
            digitalSize: design.digitalSize,
            layers: {
              create: design.layers.map((layer, index) => ({
                ...layer,
                type: new EnumType(layer.type),
                image: layer.image ? new EnumType(`$layer_${index}`) : null,
                position: layer.position,
                dimensions: layer.dimensions,
                attributes: layer.attributes,
              })),
            },
          },
        },
      },
    };

    let queryString = jsonToGraphQLQuery({ mutation }, { pretty: true, includeFalsyKeys: false });

    // appending selectors
    queryString = queryString.replace(/\}$/, `${designProperriesSelector}}`);
    const result = await backend.mutate<Mutation>({
      mutation: gql(queryString),
      variables,
    });
    commit('setDesign', result.data.createDesign);

    return result.data.createDesign;
  },
};
